// The clip catalog from mascot.json: sprite sheets, per-frame root motion, air phases, and the motion the runtime uses instead
// of the measured one where the video can't tell (running and jumping in place, climbing, pulling up onto a ledge).
// Every clip starts and ends exactly on its pose's keyframe at the root, so overriding a clip's motion can make the cat slide
// a little during that clip but never jump at the joins.

export const STABLE = new Set(['sit', 'lie', 'curl', 'back']); // poses the cat settles in for a long time

/** Motion overrides in cat heights (H): travel per cycle where the video runs in place. */
const TRAVEL = {
  run__run__run: 0.9,
};
// Gaits: playback rate of their cycle (the walk in the clips takes short, slow steps: played faster).
export const WALK_SPEED = 1.5;
const GAIT_RATE = { walk: WALK_SPEED, run: 1 };
const GAIT_CYCLE = { walk: 'walk__walk__walk', run: 'run__run__run' };
/**
 * Starting / stopping / changing gait, frame by frame as seen in the clips: the cat moves at the gait it comes from (or not at
 * all) up to frame a, eases (smoothstep) to the gait it goes to (or to a stop) by frame b, and keeps that. The playback rate does
 * the same between the two gaits' rates, so the steps keep their pace across the joins; each frame's travel = speed × its time.
 */
const GAIT_CHANGES = {
  stand_stare__start__run: { to: 'run', window: [8, 11] }, // still until 8, off at 9, running by 11
  run__stop__stand: { from: 'run', window: [12, 19] }, // running to 12, slowing, standing by 19
  run__slow__walk: { from: 'run', to: 'walk', window: [34, 50] },
  sit__start__walk: { to: 'walk', window: [13, 22] }, // getting up 0-13, first steps, walking by 22
  walk__stop__stand: { from: 'walk', window: [8, 15] },
  walk__stop__sit: { from: 'walk', window: [16, 21] }, // then turns and sits in place
  walk__stop__stand_stare: { from: 'walk', window: [10, 18] },
};
// Air frames picked by eye (the camera often follows the cat, so its height in the frame says little):
// [first frame in the air, first frame on the ground], in the built sheets' frame numbers — recheck when a clip's video is replaced.
// Clips not listed fall back to the measured height above the ground.
const AIR_FRAMES = {
  hang__drop_land__stand: [4, 27],
  hang__drop__sit: [3, 15],
  crouch__jump__stand_stare: [5, 16],
  crouch__small_jump__stand_stare: [8, 23],
  crouch__jump_fail_fall__sit: [6, 37],
  climb__short_fall__sit: [20, 26],
  climb__fall__sit: [12, 42],
  fall__fall__sit: [0, 22],
};
// Extra pull forward at the end of climbing onto a ledge (the video climbs in place)
export const ARRIVE_FORWARD = 0.35;
export const CLIMB_SPEED = 0.55; // H per second up a wall
export const RUN_CYCLE_TRAVEL = TRAVEL.run__run__run;
const smooth = (t) => (t <= 0 ? 0 : t >= 1 ? 1 : t * t * (3 - 2 * t));

export async function loadRig(manifest, H) {
  const images = new Map();
  await Promise.all(
    manifest.clips.map(
      (c) =>
        new Promise((resolve) => {
          const img = new Image();
          img.onload = () => resolve();
          img.onerror = () => (console.warn('sheet missing', c.sheet), resolve());
          img.src = `../assets/${c.sheet}`;
          images.set(c.name, img);
        }),
    ),
  );
  return rigFrom(manifest, H, (c) => images.get(c.name));
}

/** The catalog itself; imageOf gives each clip's sheet (nothing in the headless simulation). */
export function rigFrom(manifest, H, imageOf = () => null) {
  const s = H / manifest.catHeight; // display px per asset px
  const clips = new Map(manifest.clips.map((c) => [c.name, prepare(c, imageOf(c), s, H)]));
  applyGaits(clips);
  const list = [...clips.values()];
  return {
    s,
    H,
    clips,
    get: (name) => clips.get(name) ?? null,
    from: (pose) => list.filter((c) => c.from === pose),
    loops: (pose) => list.filter((c) => c.from === pose && c.to === pose && c.kind === 'loop'),
    between: (a, b) => list.filter((c) => c.from === a && c.to === b),
  };
}

function prepare(c, img, s, H) {
  const n = c.frames;
  const contact = new Array(n).fill('ground');
  for (const p of c.phases) for (let k = p.start; k < p.end && k < n; k++) contact[k] = p.contact;
  const clip = { ...c, img, s, contact, duration: n / c.fps };
  // Horizontal travel per frame in display px (facing right). Default: as measured; overridden where the video can't tell.
  const dx = new Array(n).fill(0);
  const measured = c.rootX;
  for (let k = 1; k < n; k++) dx[k] = (measured[k] - measured[k - 1]) * s;
  const total = c.kind === 'cycle' ? (measured[n] - measured[0]) * s : (measured[n - 1] - measured[0]) * s;
  if (c.kind === 'cycle' && n < measured.length) dx.push((measured[n] - measured[n - 1]) * s); // step from the last frame back to the first
  const want = TRAVEL[c.name];
  if (want !== undefined) {
    // Spread the wanted travel like the measured one if it moved forward, else evenly
    const steps = c.kind === 'cycle' ? n : n - 1;
    const fwd = total > 0.2 * H;
    for (let k = 1; k <= steps; k++) dx[k] = fwd ? (dx[k] / total) * want * H : (want * H) / steps;
  }
  clip.dx = dx;
  clip.travel = dx.slice(1).reduce((a, b) => a + b, 0); // total for a transition, per cycle for a cycle
  clip.air = airSpan(clip);
  return clip;
}

/** Speed and timing profiles for the clips that start, stop or change a gait (see GAIT_CHANGES). */
function applyGaits(clips) {
  const gait = {};
  for (const [g, name] of Object.entries(GAIT_CYCLE)) {
    const c = clips.get(name);
    if (c) gait[g] = { speed: c.travel / (c.frames / c.fps / GAIT_RATE[g]), rate: GAIT_RATE[g] }; // px per second, playback rate
  }
  for (const [name, spec] of Object.entries(GAIT_CHANGES)) {
    const c = clips.get(name);
    if (!c) continue;
    const g0 = spec.from ? gait[spec.from] : { speed: 0, rate: 1 };
    const g1 = spec.to ? gait[spec.to] : { speed: 0, rate: 1 };
    if (!g0 || !g1) continue;
    const [a, b] = spec.window;
    const n = c.frames;
    const rate = [];
    const dx = [0];
    const times = [0];
    for (let k = 0; k < n; k++) {
      const p = smooth((k - a) / Math.max(1, b - a));
      rate.push(g0.rate + (g1.rate - g0.rate) * p);
      const dt = 1 / (c.fps * rate[k]);
      times.push(times[k] + dt);
      if (k > 0) dx.push((g0.speed + (g1.speed - g0.speed) * smooth((k - 0.5 - a) / Math.max(1, b - a))) * (1 / (c.fps * rate[k - 1])));
    }
    c.dx = dx;
    c.times = times; // start time of each frame, and the end (length n + 1)
    c.duration = times[n];
    c.travel = dx.slice(1).reduce((x, y) => x + y, 0);
  }
}

/** The frames where the cat is off the ground in a jump / fall / drop: [start, end). */
function airSpan(c) {
  if (AIR_FRAMES[c.name]) return AIR_FRAMES[c.name];
  if (!/jump|fall|drop/.test(c.action)) return null;
  const up = (k) => -c.rootY[k] > 10 && c.contact[k] !== 'wall';
  if (/drop/.test(c.action)) {
    // From hang: falling from the end of the held frames to the start of the last stretch on the ground
    const heldEnd = c.contact.findIndex((p) => p !== 'held');
    let landed = c.frames;
    while (landed > 0 && c.contact[landed - 1] === 'ground') landed--;
    return landed > heldEnd + 1 ? [Math.max(0, heldEnd), landed] : [0, c.frames];
  }
  let best = null;
  let start = -1;
  let gap = 0;
  for (let k = 0; k <= c.frames; k++) {
    if (k < c.frames && up(k)) {
      if (start < 0) start = k;
      gap = 0;
    } else if (start >= 0 && (++gap > 2 || k === c.frames)) {
      const end = k - gap + 1;
      if (!best || end - start > best[1] - best[0]) best = [start, end];
      start = -1;
      gap = 0;
    }
  }
  return best;
}

/** Where frame k of a clip sits in its sprite sheet. */
export function cellOf(clip, k) {
  const { w, h, cols } = clip.cell;
  return { sx: (k % cols) * w, sy: Math.floor(k / cols) * h, w, h };
}
